import {
  Check,
  X,
  TrendingUp,
  Settings,
  Home,
  Users,
  BarChart3,
  Palette,
  Globe,
  Headphones,
} from "lucide-react";

import { motion } from "framer-motion";
import Section from "../layout/Section";
import Container from "../layout/Container";

/* ================= DATA ================= */

const items = [
  {
    label: "Pricing",
    icon: TrendingUp,
    desc: "Revenue optimisation engine",
    zenia: [
      "Dynamic daily pricing",
      "Season & demand adjustments",
      "Maximise earnings",
    ],
    others: ["Static pricing", "No demand logic", "Lost revenue"],
  },
  {
    label: "Operations",
    icon: Settings,
    desc: "End-to-end property management",
    zenia: [
      "Full operations handled",
      "Cleaning included",
      "Reliable execution",
    ],
    others: [
      "Multiple vendors",
      "Inconsistent service",
      "Manual coordination",
    ],
  },
  {
    label: "Listings",
    icon: Home,
    desc: "Optimised for bookings",
    zenia: [
      "High visibility listings",
      "Better conversion",
      "Platform optimisation",
    ],
    others: [
      "Basic listing",
      "Low optimisation",
      "Poor discoverability",
    ],
  },
  {
    label: "Guests",
    icon: Users,
    desc: "Premium guest experience",
    zenia: [
      "Fast communication",
      "Better reviews",
      "Consistent experience",
    ],
    others: [
      "Slow responses",
      "Inconsistent stays",
      "Lower ratings",
    ],
  },
  {
    label: "Reporting",
    icon: BarChart3,
    desc: "Clear performance insights",
    zenia: ["Monthly reports", "Revenue insights", "Growth tracking"],
    others: [
      "No structured reports",
      "Limited visibility",
      "No insights",
    ],
  },
  {
    label: "Design",
    icon: Palette,
    desc: "Higher perceived value",
    zenia: [
      "Interior optimisation",
      "Premium appeal",
      "Better bookings",
    ],
    others: ["No design help", "Low appeal", "Lower bookings"],
  },
  {
    label: "Distribution",
    icon: Globe,
    desc: "Maximum reach",
    zenia: [
      "All major platforms",
      "High visibility",
      "More bookings",
    ],
    others: ["Limited reach", "Low visibility", "Missed demand"],
  },
  {
    label: "Support",
    icon: Headphones,
    desc: "Dedicated support",
    zenia: ["24/7 support", "Fast resolution", "Dedicated team"],
    others: ["Limited support", "Slow replies", "No ownership"],
  },
];

/* ================= BULLETS ================= */

function BulletList({ items, tone = "zenia" }) {
  return (
    <div className="space-y-2">
      {items.map((text, idx) => (
        <div
          key={idx}
          className={`flex gap-2.5 text-small leading-relaxed ${
            tone === "zenia" ? "text-text-primary" : "text-text-muted"
          }`}
        >
          {tone === "zenia" ? (
            <Check size={13} className="mt-[2px] shrink-0 text-brand-primary" />
          ) : (
            <X size={13} className="mt-[2px] shrink-0 text-text-muted/70" />
          )}
          <span>{text}</span>
        </div>
      ))}
    </div>
  );
}

/* ================= COMPONENT ================= */

export default function ComparisonCreative() {
  return (
    <Section
      background="bg-bg-surface"
      padding="none"
      className="pt-10 pb-16 md:pt-12 md:pb-20 lg:pt-14 lg:pb-24"
    >
      <Container>

        {/* HEADER */}
        <div className="max-w-content mb-8 md:mb-10">
          <span className="text-small font-semibold uppercase tracking-[0.18em] text-brand-primary">
            Why Zenia
          </span>

          <h3 className="mt-2 text-[28px] leading-[1.05] font-heading font-extrabold text-text-primary md:text-display">
            Built for higher revenue & better stays
          </h3>

          <p className="mt-3 max-w-reading text-body text-text-muted">
            Compare how Zenia performs across key areas vs traditional management.
          </p>
        </div>

        {/* ================= DESKTOP ================= */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.35 }}
          className="hidden md:block overflow-hidden rounded-[28px] border border-border-soft bg-white shadow-soft"
        >
          <table className="w-full border-collapse text-left">
            <thead className="bg-bg-light">
              <tr>
                <th className="w-[34%] px-6 py-5 text-small font-semibold uppercase tracking-[0.16em] text-text-muted">
                  Feature
                </th>
                <th className="w-[33%] px-6 py-5 text-small font-semibold uppercase tracking-[0.16em] text-text-muted">
                  Others
                </th>
                <th className="w-[33%] px-6 py-5 text-small font-semibold uppercase tracking-[0.16em] text-brand-primary">
                  Zenia
                </th>
              </tr>
            </thead>

            <tbody>
              {items.map((item) => {
                const Icon = item.icon;

                return (
                  <tr
                    key={item.label}
                    className="border-t border-border-subtle hover:bg-bg-light/60 transition"
                  >
                    <td className="px-6 py-6 align-top">
                      <div className="flex gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-border-soft bg-bg-light text-brand-primary">
                          <Icon size={16} />
                        </div>

                        <div>
                          <div className="text-[16px] font-semibold text-text-primary">
                            {item.label}
                          </div>
                          <p className="mt-1 text-small text-text-muted">
                            {item.desc}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-6">
                      <div className="rounded-2xl border border-border-soft bg-bg-surface p-4 opacity-80">
                        <BulletList items={item.others} tone="others" />
                      </div>
                    </td>

                    <td className="px-6 py-6">
                      <div className="rounded-2xl border border-brand-primary/30 bg-brand-light p-4 shadow-card ring-1 ring-brand-primary/20">
                        <BulletList items={item.zenia} tone="zenia" />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </motion.div>

        {/* ================= MOBILE ================= */}
        <div className="md:hidden space-y-4">
          {items.map((item) => {
            const Icon = item.icon;

            return (
              <div
                key={item.label}
                className="rounded-[24px] border border-border-soft bg-white shadow-soft overflow-hidden"
              >
                <div className="bg-bg-light px-5 py-4 border-b border-border-soft flex gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-2xl border border-border-soft bg-white text-brand-primary">
                    <Icon size={16} />
                  </div>

                  <div>
                    <div className="text-[16px] font-semibold text-text-primary">
                      {item.label}
                    </div>
                    <p className="text-small text-text-muted mt-1">
                      {item.desc}
                    </p>
                  </div>
                </div>

                <div className="p-5 space-y-3">
                  <div className="rounded-2xl border border-brand-primary/30 bg-brand-light p-4 shadow-card ring-1 ring-brand-primary/20">
                    <p className="text-small font-semibold text-brand-primary mb-2">
                      Zenia
                    </p>
                    <BulletList items={item.zenia} />
                  </div>

                  <div className="rounded-2xl border border-border-soft bg-bg-surface p-4 opacity-80">
                    <p className="text-small font-semibold text-text-muted mb-2">
                      Others
                    </p>
                    <BulletList items={item.others} tone="others" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </Container>
    </Section>
  );
}